import { useContext, useEffect, useState } from "react";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { SmartSelectContact } from "@/components";
import { axiosInstance } from "@/utilities";
import { API_URL_STORE_LINKED_SELLER } from "@/services/XCONS_ENV";

/**
 * Alta de operador del seller.
 *
 * **No se pregunta nada:** el scope de la búsqueda es el seller del contexto —el que se lee arriba
 * a la izquierda— y lo resuelve el backend, que busca en sus stores vinculados y devuelve una fila
 * por persona. El código del seller no sirve como `storeCode`: los contactos son de stores y sólo
 * 32 de los 95 sellers tienen un store homónimo, así que para los otros 63 ese filtro devuelve
 * cero. Por eso el parámetro es `sellerCode` y la traducción a stores pasa del otro lado.
 *
 * Lo único que este panel resuelve es **dónde vive la ficha** cuando se da de alta a alguien que
 * todavía no existe: se usa el store homónimo del seller si existe y si no el primer vinculado
 * activo. Va en silencio y sin bloquear la pantalla — es un detalle del alta, no una decisión del
 * operador — y evita que la ficha nazca con `store_code = <código del seller>`, que no es ningún
 * store.
 */
export const SellersUserPermissionsCreate: React.FC<{
  t?: any;
  onClose?: () => void;
  refetch?: () => void;
  sectionType?: "permission" | "contact";
}> = ({ t, onClose, refetch, sectionType }) => {
  const { smartMenuName } = useContext(SmartMenuContext);
  const sellerName = smartMenuName?.replace(/"/g, "").toLowerCase();
  const sellerCode = encodeURIComponent(sellerName);

  const [fichaStoreCode, setFichaStoreCode] = useState<string>("");

  useEffect(() => {
    let active = true;
    if (!sellerCode) return;

    axiosInstance
      .get(`${API_URL_STORE_LINKED_SELLER}/${sellerCode}/stores`, {
        params: { "filter.storeLinkedSellers.active": "$eq:1", limit: 100 },
      })
      .then(({ data }) => {
        if (!active) return;
        const rows: any[] = Array.isArray(data) ? data : data?.data ?? [];
        const linked = rows
          // El endpoint lista todos los stores y marca cuáles están vinculados: el filtro del
          // request se repite acá porque una relación no cargada no es una relación inactiva.
          .filter((r) => r?.storeLinkedSellers?.active)
          .map((r) => String(r.storeCode ?? "").trim())
          .filter(Boolean);

        setFichaStoreCode(linked.find((code) => code === sellerName) ?? linked[0] ?? "");
      })
      .catch(() => active && setFichaStoreCode(""));

    return () => {
      active = false;
    };
  }, [sellerCode, sellerName]);

  const handleCancel = () => {
    onClose?.();
  };

  return (
    <SmartSelectContact
      handleCancel={handleCancel}
      onSaveContact={async (data: any) => {
        console.log("Contact saved:", data);
      }}
      store={{
        code: sellerCode,
        name: smartMenuName,
        link: "seller",
      }}
      // El cargo se da sobre el seller; la búsqueda va por los permisos del operador logueado y la
      // ficha nueva, a un store.
      contactStores={{ sellerCode: sellerName, fichaStoreCode }}
      layout="modal"
      refetch={refetch}
      sectionType={sectionType}
    />
  );
};
